import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { B2C } from 'cosmos-components';

import { SciContent } from 'src/components/shared/SciContent';
import { SciPage } from 'src/components/shared/SciPage';
import { SciPagesEnum } from 'src/components/shared/SciPage/types';
import {
    setCarouselDotsCurrentPage,
    setCarouselPageIsCompletedAction,
} from 'src/components/shared/SciPage/SciPage.duck';
import {
    sciBrandNameSelector,
    sciLanguageSelector,
} from 'src/components/shared/SciPage/SciPage.selector';
import { sciContentKeys } from 'src/constants';
import { CaBrandNames } from 'src/enums';
import * as SShared from 'src/styles/shared.styled';
import globalMessages from 'src/messages';
import { useGoBack } from 'src/hooks/useGoBack';
import { colors } from 'src/themes';

import { MODULE_PREFIX } from './documents.constants';

import msg from './documents.messages';
import * as S from './documents.style';
import { useDocuments } from './hooks/documents.hooks';
import { DocumentRowComponent } from './documentRow.component';
import { Section } from './documents.types';

export const componentDocuments = `${MODULE_PREFIX}-component`;

export const DocumentsComponent: FC = () => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const { goBack } = useGoBack();
    const { data, isLoading, isPostingDocuments, onValidateSection } =
        useDocuments();
    const language = useSelector(sciLanguageSelector);
    const brandName = useSelector(sciBrandNameSelector);

    const isCanadream = brandName === CaBrandNames.canadream;

    useEffect(() => {
        dispatch(setCarouselDotsCurrentPage(SciPagesEnum.Documents));
    }, [dispatch]);

    const fieldSchema = yup.object().shape(
        data.reduce((acc, section: Section) => {
            return {
                ...acc,
                [section.id.sectionId]: yup
                    .boolean()
                    .oneOf([true], intl.formatMessage(msg.errors.required))
                    .required(intl.formatMessage(msg.errors.required)),
            };
        }, {}),
    );

    const { control, handleSubmit, setValue, trigger, clearErrors } = useForm<any>({
        resolver: yupResolver(fieldSchema),
        mode: 'onChange',
    });

    const onSubmit = handleSubmit(() => {
        dispatch(
            setCarouselPageIsCompletedAction({
                page: SciPagesEnum.Documents,
            }),
        );
    });

    useEffect(() => {
        for (const section of data) {
            const valid =
                !!section.inputs &&
                section.inputs.every((input) => input.value);
            setValue(section.id.sectionId as any, valid);

            if (valid) {
                trigger(section.id.sectionId as any);
            }
        }
    }, [JSON.stringify(data)]);

    useEffect(() => {
        clearErrors();
    }, [language]);

    return (
        <SciPage>
            <S.Container data-test-id={componentDocuments}>
                <S.Header>
                    <S.Title>{intl.formatMessage(msg.pageItems.title)}</S.Title>
                    <S.StyleContainedModal
                        dialogInfo={
                            <SciContent
                                sciContentKey={
                                    sciContentKeys.sciInfoBoxDocuments
                                }
                            />
                        }
                    />
                </S.Header>
                <SciContent sciContentKey={sciContentKeys.sciDocumentsPage} />
            </S.Container>
            <S.HeaderContainer>
                <S.SubTitle>
                    {intl.formatMessage(msg.pageItems.subtitle)}
                </S.SubTitle>
                <span>
                    {intl.formatMessage(
                        isCanadream
                            ? msg.pageItems.informationCa
                            : msg.pageItems.information,
                    )}
                </span>
            </S.HeaderContainer>
            <B2C.LoaderContainer
                loading={isPostingDocuments || isLoading}
                size={'large'}
                opacity={{ opacity: '90%', color: colors.white }}
            >
                <form onSubmit={onSubmit}>
                    {data?.map((section) => (
                        <Controller
                            name={section.id.sectionId}
                            control={control}
                            key={`document-row-${section.id.sectionId}`}
                            render={({ fieldState: { error } }) => (
                                <DocumentRowComponent
                                    section={section}
                                    error={error?.message}
                                    onValidateSection={onValidateSection}
                                />
                            )}
                        />
                    ))}
                    <SShared.ActionWrapper>
                        <SShared.StyledSecondaryButton
                            data-test-id={`${MODULE_PREFIX}-back`}
                            type="button"
                            onClick={goBack}
                        >
                            {intl.formatMessage(globalMessages.buttons.back)}
                        </SShared.StyledSecondaryButton>
                        <SShared.StyledPrimaryButton
                            data-test-id={`${MODULE_PREFIX}-save`}
                            type="submit"
                        >
                            {intl.formatMessage(msg.buttons.continue)}
                        </SShared.StyledPrimaryButton>
                    </SShared.ActionWrapper>
                </form>
            </B2C.LoaderContainer>
        </SciPage>
    );
};
